import { useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock, Meter, SectionCard, Sparkline } from "../../components/primitives";
import { formatCount, formatMs, formatPercent } from "../../format";
import { NEUTRAL } from "../../palette";
import type { PipelineBranch, PipelineSection } from "../../types";
import { invalidTone } from "./helpers";
import { BranchFlags, InlineStat, ProblemCount } from "./shared";

type SortKey = "name" | "orders" | "captured" | "stuck" | "invalid" | "nok" | "latency" | "lastCapture";
const SORT_KEYS: SortKey[] = ["captured", "orders", "stuck", "invalid", "nok", "latency", "lastCapture", "name"];

const sortValue = (b: PipelineBranch, key: SortKey): number | string | null => {
  switch (key) {
    case "name":
      return b.name.toLocaleLowerCase();
    case "orders":
      return b.orders;
    case "captured":
      return b.captured;
    case "stuck":
      return b.verdicts.missing;
    case "invalid":
      return b.invalid_share;
    case "nok":
      return b.nok_share;
    case "latency":
      return b.capture_to_verdict?.p95 ?? null;
    case "lastCapture":
      return b.last_capture_at;
  }
};

/** Per-branch pizza flow of the window; sortable, a table from md up and cards below. */
export function BranchesCard({ data }: { data: PipelineSection }) {
  const { t } = useLocalization();
  const [sortKey, setSortKey] = useState<SortKey>("captured");
  const [desc, setDesc] = useState(true);

  const sorted = useMemo(() => {
    const out = [...data.branches];
    out.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      // missing values always last
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = typeof va === "string" && typeof vb === "string" ? va.localeCompare(vb) : Number(va) - Number(vb);
      return desc ? -cmp : cmp;
    });
    return out;
  }, [data.branches, sortKey, desc]);

  // branches that capture photos but had nothing at all in the window
  const quiet = useMemo(() => {
    const active = new Set(data.branches.map((b) => b.id));
    return data.branches_meta.filter((b) => b.enable_acquisition && !active.has(b.id));
  }, [data.branches, data.branches_meta]);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) setDesc((d) => !d);
    else {
      setSortKey(key);
      setDesc(key !== "name");
    }
  };

  const header = (key: SortKey, children: ReactNode, className?: string) => (
    <TableHead className={cn("h-8", className)} aria-sort={sortKey === key ? (desc ? "descending" : "ascending") : undefined}>
      <button
        type="button"
        onClick={() => sortBy(key)}
        className={cn("inline-flex items-center gap-1 hover:text-foreground", sortKey === key && "text-foreground")}
      >
        {children}
        {sortKey === key && (desc ? <ArrowDown className="h-3 w-3" aria-hidden /> : <ArrowUp className="h-3 w-3" aria-hidden />)}
      </button>
    </TableHead>
  );

  return (
    <SectionCard
      title={t("monitoring.pipeline.branches.title")}
      description={t("monitoring.pipeline.branches.description", { window: t(`monitoring.window.long.${data.window}`) })}
      actions={
        <div className="flex items-center gap-1.5">
          <Select value={sortKey} onValueChange={(value) => setSortKey(value as SortKey)}>
            <SelectTrigger className="h-8 w-[11.5rem] text-xs" aria-label={t("monitoring.pipeline.branches.sort")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_KEYS.map((key) => (
                <SelectItem key={key} value={key} className="text-xs">
                  {t(`monitoring.pipeline.branches.sort.${key}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setDesc((d) => !d)}
            aria-label={desc ? t("monitoring.pipeline.branches.desc") : t("monitoring.pipeline.branches.asc")}
            title={desc ? t("monitoring.pipeline.branches.desc") : t("monitoring.pipeline.branches.asc")}
          >
            {desc ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}
          </Button>
        </div>
      }
      contentClassName="p-3 sm:p-4"
    >
      {sorted.length === 0 ? (
        <EmptyBlock>{t("monitoring.noData")}</EmptyBlock>
      ) : (
        <>
          <div className="hidden md:block">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {header("name", t("monitoring.col.branch"), "min-w-[9rem]")}
                  {header("orders", t("monitoring.series.orders"), "text-right")}
                  {header("captured", t("monitoring.series.captured"), "text-right")}
                  <TableHead className="h-8">{t("monitoring.pipeline.branches.verdicts")}</TableHead>
                  {header("invalid", t("monitoring.pipeline.branches.invalidShare"))}
                  {header("nok", t("monitoring.pipeline.branches.nokShare"), "hidden text-right min-[1140px]:table-cell")}
                  {header("latency", t("monitoring.pipeline.branches.toVerdict"), "hidden text-right xl:table-cell")}
                  <TableHead className="hidden h-8 text-right xl:table-cell">{t("monitoring.pipeline.branches.uploadDelay")}</TableHead>
                  {header("lastCapture", t("monitoring.cameras.seen.lastCapture"), "hidden min-[1140px]:table-cell")}
                  <TableHead className="hidden h-8 w-28 min-[1400px]:table-cell">{t("monitoring.pipeline.branches.trend")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((b) => (
                  <TableRow key={b.id ?? "unknown"} className={cn("align-top", b.verdicts.missing > 0 && "bg-red-500/[0.04]")}>
                    <TableCell className="py-2">
                      <div className="break-words text-sm leading-snug">{b.name}</div>
                      <BranchFlags acquisition={b.enable_acquisition} bbTrigger={b.enable_bb_trigger} />
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono tabular-nums">{formatCount(b.orders)}</TableCell>
                    <TableCell className="py-2 text-right">
                      <div className="font-mono tabular-nums">{formatCount(b.captured)}</div>
                      <div className="whitespace-nowrap text-[10px] text-muted-foreground">
                        {t("monitoring.pipeline.branches.evaluated", { count: formatCount(b.evaluated) })}
                      </div>
                      {b.recaptures > 0 && (
                        <div className="whitespace-nowrap text-[10px] text-muted-foreground">
                          {t("monitoring.pipeline.branches.recaptures", { count: formatCount(b.recaptures) })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <VerdictCounts branch={b} />
                    </TableCell>
                    <TableCell className="py-2">
                      <InvalidShare share={b.invalid_share} />
                    </TableCell>
                    <TableCell className="hidden py-2 text-right font-mono tabular-nums min-[1140px]:table-cell">{formatPercent(b.nok_share)}</TableCell>
                    <TableCell className="hidden py-2 text-right font-mono tabular-nums xl:table-cell">{formatMs(b.capture_to_verdict?.p95)}</TableCell>
                    <TableCell className="hidden py-2 text-right font-mono tabular-nums xl:table-cell">{formatMs(b.upload_delay?.p95)}</TableCell>
                    <TableCell className="hidden whitespace-nowrap py-2 min-[1140px]:table-cell">
                      <Ago ts={b.last_capture_at} />
                    </TableCell>
                    <TableCell className="hidden py-2 min-[1400px]:table-cell">
                      <Sparkline values={b.series} bars color={NEUTRAL.strong} height={20} className="w-28" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="flex flex-col gap-2 md:hidden">
            {sorted.map((b) => (
              <li key={b.id ?? "unknown"} className={cn("rounded-lg border p-3", b.verdicts.missing > 0 && "border-red-500/40")}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="break-words text-sm font-medium leading-snug">{b.name}</div>
                    <BranchFlags acquisition={b.enable_acquisition} bbTrigger={b.enable_bb_trigger} />
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    <Ago ts={b.last_capture_at} />
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 border-t border-border/50 pt-2 text-xs">
                  <MiniStat label={t("monitoring.series.orders")} value={formatCount(b.orders)} />
                  <MiniStat label={t("monitoring.series.captured")} value={formatCount(b.captured)} />
                  <MiniStat label={t("monitoring.pipeline.totals.evaluated")} value={formatCount(b.evaluated)} />
                </div>
                <div className="mt-2">
                  <VerdictCounts branch={b} />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-muted-foreground">{t("monitoring.pipeline.branches.invalidShare")}</span>
                    <InvalidShare share={b.invalid_share} />
                  </div>
                  <InlineStat label={t("monitoring.pipeline.branches.nokShare")}>{formatPercent(b.nok_share)}</InlineStat>
                  <InlineStat label={t("monitoring.pipeline.branches.toVerdict")}>{formatMs(b.capture_to_verdict?.p95)}</InlineStat>
                  <InlineStat label={t("monitoring.pipeline.branches.uploadDelay")}>{formatMs(b.upload_delay?.p95)}</InlineStat>
                </div>
                <Sparkline values={b.series} bars color={NEUTRAL.strong} height={20} className="mt-2" />
              </li>
            ))}
          </ul>
        </>
      )}
      {quiet.length > 0 && (
        <p className="mt-3 break-words text-[11px] text-muted-foreground">
          {t("monitoring.pipeline.branches.quiet", { names: quiet.map((b) => b.name || b.id).join(", ") })}
        </p>
      )}
    </SectionCard>
  );
}

function MiniStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="break-words text-[10px] text-muted-foreground">{label}</div>
      <div className="font-mono text-sm tabular-nums">{value}</div>
    </div>
  );
}

function InvalidShare({ share }: { share: number | null }) {
  const tone = invalidTone(share);
  return (
    <div className="flex w-20 flex-col gap-1">
      <span
        className={cn(
          "font-mono tabular-nums",
          tone === "nok" ? "font-semibold text-red-400" : tone === "warn" ? "text-amber-300" : undefined,
        )}
      >
        {formatPercent(share)}
      </span>
      {share !== null && <Meter value={share} tone={tone} />}
    </div>
  );
}

/** OK calm, problems coloured only when there are any */
function VerdictCounts({ branch }: { branch: PipelineBranch }) {
  const { t } = useLocalization();
  const v = branch.verdicts;
  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-xs">
      <InlineStat label={t("monitoring.verdict.ok")}>
        <span className="text-muted-foreground">{formatCount(v.ok)}</span>
      </InlineStat>
      <InlineStat label={t("monitoring.verdict.nok")}>
        <ProblemCount value={v.nok} tone="warn" />
      </InlineStat>
      <InlineStat label={t("monitoring.pipeline.branches.photo")} title={t("monitoring.pipeline.totals.photoDetail", {
        invalid: formatCount(v.invalid_photo),
        noPizza: formatCount(v.no_pizza),
        share: formatPercent(branch.invalid_share),
      })}>
        <ProblemCount value={v.invalid_photo + v.no_pizza} tone="warn" />
      </InlineStat>
      <InlineStat label={t("monitoring.verdict.missing")}>
        <ProblemCount value={v.missing} />
      </InlineStat>
      {v.pending > 0 && (
        <InlineStat label={t("monitoring.verdict.pending")}>
          <span className="text-muted-foreground">{formatCount(v.pending)}</span>
        </InlineStat>
      )}
    </div>
  );
}
